<template>
  <modal :name="name" :width="480" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" :clickToClose="false" @before-open="initialize" @before-close="beforeClose" @opened="focusHeading" @closed="$emit('closed')">
    <div class="popup booking-invoice-modal" :aria-busy="busy || uploading" @keydown.esc.prevent.stop="close">
      <button class="popup__close" type="button" aria-label="Close edit invoice" :disabled="busy || uploading" @click="close" />
      <h2 ref="heading" class="popup__title" tabindex="-1">Edit invoice</h2>
      <div class="popup__body">
        <form novalidate @submit.prevent="submit">
          <fieldset class="booking-invoice-modal__fields" :disabled="busy || uploading || saved || uncertain || !permitted">
            <app-form-group label="Total Amount" :label-for="`${name}-total`" required>
              <app-input :id="`${name}-total`" v-model="draft.total_amount" name="total_amount" inputmode="decimal" placeholder="0.00" :error="Boolean(fieldErrors.total_amount)" @input="clearError('total_amount')" />
              <template #error><div v-if="fieldErrors.total_amount">{{ fieldErrors.total_amount }}</div></template>
            </app-form-group>
            <app-form-group label="Number" :label-for="`${name}-number`">
              <app-input :id="`${name}-number`" v-model="draft.invoice_number" name="invoice_number" placeholder="Enter invoice number" :maxlength="100" :error="Boolean(fieldErrors.invoice_number)" @input="clearError('invoice_number')" />
              <template #error><div v-if="fieldErrors.invoice_number">{{ fieldErrors.invoice_number }}</div></template>
            </app-form-group>
            <app-form-group label="Attachment" required>
              <app-uploader :key="`${sessionId}-${uploaderRevision}`" v-model="draft.attachment" :single-file="true" :readonly="busy || uploading || saved || uncertain || !permitted" @uploading="uploading = $event" @onAddedFile="attachmentChanged" @upload-error="uploadError">
                <template #action="{ filePresent }"><div><button v-if="!filePresent" type="button" class="booking-invoice-modal__upload dz-message"><i class="pi pi-paperclip" aria-hidden="true" /> Attach the file</button></div></template>
                <template #remove-icon><i class="pi pi-times" aria-hidden="true" /><span class="booking-invoice-modal__sr">Remove invoice file</span></template>
              </app-uploader>
              <p v-if="uploading" class="booking-invoice-modal__uploading" role="status">Uploading invoice…</p>
              <template #error><div v-if="fieldErrors.attachment">{{ fieldErrors.attachment }}</div></template>
            </app-form-group>
          </fieldset>
          <p v-if="saved" class="booking-invoice-modal__status" role="status">Invoice saved. {{ reloading ? 'Updating invoice…' : 'Reload the invoice to see the latest details.' }}</p>
          <p v-if="uncertain" class="booking-invoice-modal__error" role="alert">The save result could not be verified. Reload the invoice before continuing.</p>
          <p v-if="error" class="booking-invoice-modal__error" role="alert">{{ error }}</p>
          <footer class="booking-invoice-modal__footer">
            <app-button theme="dark" :disabled="busy || uploading" @click.native="close">Cancel</app-button>
            <app-button v-if="saved || uncertain" :disabled="busy || uploading" @click.native="reloadInvoice">{{ reloading ? 'Updating…' : 'Reload invoice' }}</app-button>
            <app-button v-else type="submit" :disabled="!canSubmit">{{ saving ? 'Saving…' : uploading ? 'Uploading…' : 'Save' }}</app-button>
          </footer>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { getFlightsBookingInvoice, patchFlightsBookingInvoice } from '@/http'
import { responseError } from './booking-detail'
import { canEditBookingBlock } from './booking-edits'
import { invoiceDraft, invoiceErrors, invoicePayload } from './booking-invoice'

export default {
  name: 'BookingInvoiceModal',
  props: { name: { type: String, required: true }, booking: { type: Object, required: true }, invoice: { type: Object, required: true } },
  data() {
    return { draft: { total_amount: '', invoice_number: '', attachment: {} }, fieldErrors: {}, error: '', saving: false, reloading: false, uploading: false, saved: false, uncertain: false, sessionId: 0, uploaderRevision: 0 }
  },
  computed: {
    permitted() { return canEditBookingBlock(this.booking, this.$store.state.userGroups, 'invoice') },
    busy() { return this.saving || this.reloading },
    canSubmit() { return this.permitted && !this.busy && !this.uploading && !this.saved && !this.uncertain }
  },
  beforeDestroy() { this.sessionId++ },
  methods: {
    initialize() {
      this.sessionId++
      this.draft = invoiceDraft(this.invoice)
      this.fieldErrors = {}
      this.error = ''
      this.saving = false
      this.reloading = false
      this.uploading = false
      this.saved = false
      this.uncertain = false
    },
    clearError(field) { this.$delete(this.fieldErrors, field) },
    attachmentChanged(file) {
      this.clearError('attachment')
      // Reset the single-file queue after removal or a failed upload.
      if (!file || !Object.keys(file).length) this.uploaderRevision++
    },
    uploadError(message) { this.$set(this.fieldErrors, 'attachment', message) },
    async submit() {
      if (!this.canSubmit) return
      this.fieldErrors = invoiceErrors(this.draft)
      if (Object.keys(this.fieldErrors).length) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      this.saving = true
      this.error = ''
      try {
        const { data } = await patchFlightsBookingInvoice(bookingId, invoicePayload(this.draft), { localErrors: true })
        if (sessionId !== this.sessionId) return
        this.saved = true
        if (data && data.booking_id === bookingId) this.$emit('updated', data)
        this.$notify({ type: 'success', title: 'Invoice saved.' })
        this.saving = false
        await this.reloadInvoice()
      } catch (error) {
        if (sessionId === this.sessionId) {
          this.error = responseError(error, 'Unable to save invoice.')
          this.uncertain = !error.response || error.response.status >= 500
        }
      } finally {
        if (sessionId === this.sessionId) this.saving = false
      }
    },
    async reloadInvoice() {
      if (this.busy || this.uploading) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      this.reloading = true
      this.error = ''
      try {
        const { data } = await getFlightsBookingInvoice(bookingId, { localErrors: true })
        if (sessionId !== this.sessionId) return
        if (!data || data.booking_id !== bookingId) throw new Error('Invalid invoice response')
        this.$emit('updated', data)
        this.reloading = false
        this.close()
      } catch (error) {
        if (sessionId === this.sessionId) this.error = responseError(error, 'Unable to reload invoice.')
      } finally {
        if (sessionId === this.sessionId) this.reloading = false
      }
    },
    focusHeading() { if (this.$refs.heading) this.$refs.heading.focus() },
    close() { if (!this.busy && !this.uploading) this.$modal.hide(this.name) },
    beforeClose(event) {
      if (this.busy || this.uploading) event.cancel()
      else this.sessionId++
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-invoice-modal .popup__title { text-align: left; cursor: default; }
.booking-invoice-modal__fields { min-width: 0; margin: 0; padding: 0; border: 0; }
.booking-invoice-modal__fields:disabled { opacity: .7; }
.booking-invoice-modal__upload { display: inline-flex; align-items: center; gap: 8px; padding: 11px 14px; border: 0; border-radius: 6px; background: $color-theme; color: #fff; font: inherit; font-size: 13px; cursor: pointer; }
.booking-invoice-modal__uploading { margin: 8px 0 0; color: $color-text-second; font-size: 13px; }
.booking-invoice-modal__status { margin: 16px 0; color: $color-theme; font-size: 13px; }
.booking-invoice-modal__error { margin: 16px 0; color: $color-error; font-size: 13px; overflow-wrap: anywhere; }
.booking-invoice-modal__footer { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 12px; margin-top: 24px; }
.booking-invoice-modal__footer .btn { min-width: 100px; }
.booking-invoice-modal__sr { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; }
.booking-invoice-modal .popup__close:focus-visible, .booking-invoice-modal__upload:focus-visible { outline: 2px solid $color-theme; outline-offset: 3px; }
</style>
